import styled from '@emotion/styled';
import { api } from '@lcdev/fetch';
import { motion } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { AccentButton } from '../components/button';
import { Section, SectionContent } from '../components/utility/section';
import Loader from '../components/utility/loader';
import SEO from '../components/seo';
import { getGoogleClickIdentifier } from '../components/utility/google-click-identifier';
import { defaultTransition } from '../styles/animation';

// TODO: Move to app-config
const contactApi = api(
  'https://prod-17.canadacentral.logic.azure.com:443/workflows/9723c488e6fb41618151c89c318346dc/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=EVLbT5l56PBSXE0P4qlQAQUBTthBPce9pCWooH-Db7w',
);

const ContactPage = () => {
  const { register, handleSubmit } = useForm();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gclid, setGCLID] = useState('');

  useEffect(() => {
    setTimeout(() => {
      // Get the tracker information after a small delay to allow it to be loaded
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      setGCLID(getGoogleClickIdentifier());
    }, 100);
  }, []);

  return (
    <>
      <SEO title="Contact" />
      <ContactUsContainer>
        <SectionContent>
          <motion.h1
            animate={{ x: 0, opacity: 1 }}
            initial={{ x: 50, opacity: 0 }}
            transition={defaultTransition}
          >
            Yesss! You clicked the button{' '}
            <Emoticon
              animate={{ scale: 1, opacity: 1 }}
              initial={{ scale: 0, opacity: 0 }}
              transition={{
                ...defaultTransition,
                delay: 0.2,
              }}
            >
              🎉
            </Emoticon>
          </motion.h1>
          {!isSubmitted && (
            <motion.p
              animate={{ x: 0, opacity: 1 }}
              initial={{ x: 50, opacity: 0 }}
              transition={{
                ...defaultTransition,
                delay: 0.1,
              }}
            >
              We'd love to work with you, so tell us a little bit about yourself.
            </motion.p>
          )}
          {!isSubmitted && !isLoading && (
            <Form
              onSubmit={handleSubmit(async (fields) => {
                setIsLoading(true);
                await contactApi.post('/').withJsonBody(fields);
                if (typeof window !== 'undefined') {
                  const anyWindow = window as any;
                  if (anyWindow.gtag !== null) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    anyWindow.gtag('event', 'generate_lead', { currency: 'CAD', value: 100 });
                  }
                }
                setIsLoading(false);
                setIsSubmitted(true);
              })}
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{
                ...defaultTransition,
                delay: 0.2,
              }}
            >
              <FormSection>
                <input ref={register({ required: true })} name="name" placeholder="Name" />
                <input
                  ref={register({ required: true })}
                  name="email"
                  placeholder="Email address"
                />
                <input ref={register({ required: true })} name="phone" placeholder="Phone number" />
              </FormSection>
              <FormSection>
                <textarea
                  ref={register({ required: true })}
                  name="message"
                  placeholder="Your idea, question, or message"
                />
              </FormSection>
              <FormSection>
                <input
                  ref={register({ required: false })}
                  name="gclid"
                  value={gclid}
                  type="hidden"
                />
              </FormSection>
              <FormSection>
                <AccentButton type="submit">Send</AccentButton>
              </FormSection>
            </Form>
          )}
          {isSubmitted && (
            <PostSubmitMessage>
              <motion.span
                animate={{ x: 0, opacity: 1 }}
                initial={{ x: 50, opacity: 0 }}
                transition={defaultTransition}
              >
                <Emoticon>👋</Emoticon> Sweet! A bunch of people just got an email.
                <br />
                One of us will get back to you right away.
              </motion.span>
            </PostSubmitMessage>
          )}
          {isLoading && <Loader />}
        </SectionContent>
      </ContactUsContainer>
    </>
  );
};

const ContactUsContainer = styled(Section)`
  margin-bottom: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 4rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    margin-bottom: 6rem;
  }
`;

const Form = styled(motion.form)`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-top: 5rem;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
  }

  input,
  textarea {
    width: 100%;
    margin-right: 2rem;
    padding: 1rem;
    margin-bottom: 1.5rem;
    border: 1px solid rgb(118, 118, 118);
    border-radius: 0.2rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      margin-bottom: 0;
    }

    :last-of-type {
      margin-right: 0;
    }
  }

  textarea {
    min-width: 100%;
    height: 12rem;
  }

  :last-of-type {
    margin-bottom: 0;
  }
`;

const PostSubmitMessage = styled.div`
  margin-top: 3rem;

  span {
    font-size: 3rem;
    line-height: 1.5;
    font-weight: 300;
    display: inline-block;
  }
`;

const Emoticon = styled(motion.div)`
  display: inline-block;
`;

export default ContactPage;
